import { useEffect, useState, ChangeEvent } from "react";
import {
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Box,
  IconButton, Typography, Divider, Tooltip,
} from "@mui/material";
import { useApi, useConfiguration } from "system/hooks";
import { useAuth } from "system/hooks/use-auth";
import { authStorage, isTokenValid } from "components/Intune/AuthService";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from 'react-redux';
import IRootState from "system/types/store/IRootState";
import { RootState } from "system/store";
import { intuneActions } from "system/store/slices/intune-slice";
import { TenantData } from "system/types/wireTypes";

const MAX_TENANT_NAME_LENGTH = 100;
const SELECTED_TENANT_NAME_DISPLAY_LENGTH = 50;

const truncate = (value: string, maxLength: number) => (value.length > maxLength ? `${value.slice(0, maxLength)}…` : value);


export function IntuneToggle() {
  const { currentTenant, roles, isGlobalAdmin } = useConfiguration();
  const currentTenantId = useSelector((state: IRootState) => state.config.currentTenantId);

  const dispatch = useDispatch();
  const isIntuneOn = useSelector((state: RootState) => state.intune.isIntuneOn);
  const [tenantPopupOpen, setTenantPopupOpen] = useState(false);
  const [allowedTenants, setAllowedTenants] = useState<TenantData[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const selectedTenant = useSelector((state: RootState) => state.intune.selectedTenant);
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingTenantId, setSavingTenantId] = useState<string | null>(null);


  const api = useApi();
  const { getToken, loginToTenant, logoutIntune, tryAutoLogin, handleRedirect } =
    useAuth();

  // Commits a tenant to both Redux and authStorage together. Only safe to call once the
  // tenant is actually confirmed active (disabling, or renaming the already-connected
  // tenant) — a fresh selection that's about to redirect must NOT go through here, since
  // loginToTenant's redirect can fail before it navigates away, and by then this would have
  // already told Redux the tenant is selected even though no login occurred.
  const selectTenant = (tenant: TenantData | null) => {
    dispatch(intuneActions.setSelectedTenant(tenant));
    authStorage.setTenantDisplayName(tenant?.tenantDisplayName ?? null);
  };

  // Reads tenantId/displayName back out of authStorage's sessionStorage-backed keys.
  // Guarded so it's a no-op once Redux already matches storage — restoreSelectedTenant
  // builds a fresh object on every call, and dispatching that unconditionally on every
  // (unmemoized) effect run was what caused the render loop.
  const restoreSelectedTenant = (tenantId: string) => {
    const tenantDisplayName = authStorage.getTenantDisplayName();
    if (selectedTenant?.tenantId === tenantId && selectedTenant?.tenantDisplayName === tenantDisplayName) {
      return;
    }

    dispatch(intuneActions.setSelectedTenant({
      tenantId,
      tenantDisplayName,
    }));
  };

  useEffect(() => {
    const checkTokenAndAutoLogin = async () => {
      const tenant = authStorage.getTenantId();
      const account = authStorage.getHomeAccountId();

      //Only run if a previous session exists
      if (!tenant || !account) return;

      // Check whether a usable token is already available (e.g. survived a fast
      // remount) before falling back to re-authenticating.
      const token = await getToken();
      if (token && isTokenValid(token)) {
        dispatch(intuneActions.setIntuneOn(true));
        restoreSelectedTenant(tenant);
        return;
      }

      //Prevent infinite redirect loop
      if (!authStorage.getAutoLoginAttempted()) {
        authStorage.setAutoLoginAttempted();

        const success = await tryAutoLogin();
        if (success) {
          dispatch(intuneActions.setIntuneOn(true));
          restoreSelectedTenant(tenant);
        } else {
          console.warn(
            "Intune auto-login did not complete (silent token acquisition and redirect fallback both failed). " +
            "Leaving Intune off for the rest of this session — toggle it back on to retry."
          );
          dispatch(intuneActions.setIntuneOn(false));
        }
      }
    };

    checkTokenAndAutoLogin();
  }, [tryAutoLogin, getToken, dispatch]);

  useEffect(() => {
    (async () => {
      const success = await handleRedirect();
      if (success) {
        dispatch(intuneActions.setIntuneOn(true));
        authStorage.clearAutoLoginAttempted();

        const tenantId = authStorage.getTenantId();
        if (tenantId) restoreSelectedTenant(tenantId);
      }
    })();
  }, [handleRedirect, dispatch]);

  const handleEnable = async () => {
    setErrorMessage("");

    try {
      const token = await getToken();

      if (token && isTokenValid(token)) {
        dispatch(intuneActions.setIntuneOn(true));
        return;
      }

      // No usable token — reset the switch now rather than leaving it visually "on"
      // (from the native click) for the duration of the tenant lookup/login below.
      dispatch(intuneActions.setIntuneOn(false));

      if (!currentTenantId) {
        setErrorMessage("No tenant selected. Select a tenant before enabling Intune.");
        return;
      }

      const response = await api.intune.getTenants(currentTenantId);
      const tenants = response.items || [];

      setAllowedTenants(tenants);
      // Always show the picker, even for a single tenant, so an admin can edit its display name.
      setTenantPopupOpen(true);
    } catch (err) {
      console.error("Failed to enable Intune access", err);
      dispatch(intuneActions.setIntuneOn(false));
      setErrorMessage("Failed to enable Intune access. Please try again.");
    }
  };

  const handleDisable = () => {
    logoutIntune();
    dispatch(intuneActions.setIntuneOn(false));
    selectTenant(null);
    setErrorMessage("");
  };

  const handleToggle = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      await handleEnable();
    } else {
      handleDisable();
    }
  };

  const handleSelectTenant = async (tenant: TenantData) => {
    setTenantPopupOpen(false);
    // Persist the display name now so it survives loginToTenant's full-page redirect —
    // but don't commit to Redux yet. loginRedirect can fail before it ever navigates away
    // (misconfigured client, blocked cookies, invalid tenant), and swallows that failure
    // internally, so committing selectedTenant here would claim a connection that never
    // happened. The Redux commit instead happens in the handleRedirect effect above, once
    // the login is actually confirmed successful — same pattern as the mount-time restores.
    authStorage.setTenantDisplayName(tenant.tenantDisplayName ?? null);
    await loginToTenant(tenant.tenantId);
  };

  const startEditing = (tenant: TenantData) => {
    setEditingTenantId(tenant.tenantId);
    setEditValue(tenant.tenantDisplayName ?? "");
  };

  const cancelEditing = () => {
    setEditingTenantId(null);
    setEditValue("");
  };

  const saveTenantDisplayName = async (tenant: TenantData) => {
    if (!currentTenantId || editValue.length > MAX_TENANT_NAME_LENGTH) return;

    setSavingTenantId(tenant.tenantId);
    try {
      const trimmed = editValue.trim();
      const response = await api.intune.updateTenantDisplayName(
        currentTenantId,
        tenant.tenantId,
        trimmed.length > 0 ? trimmed : null
      );

      if (!response?.isSuccess || !response.item) {
        setErrorMessage(response?.failMessage || "Failed to update tenant name. Please try again.");
        return;
      }

      const updated = response.item;
      setAllowedTenants(prev => prev.map(t => (t.tenantId === tenant.tenantId ? updated : t)));
      if (selectedTenant?.tenantId === tenant.tenantId) {
        selectTenant(updated);
      }
      setEditingTenantId(null);
      setEditValue("");
    } catch (err) {
      console.error("Failed to update tenant display name", err);
      setErrorMessage("Failed to update tenant name. Please try again.");
    } finally {
      setSavingTenantId(null);
    }
  };

  return (
    <>
      <FormControlLabel
        control={
          <Tooltip title={isIntuneOn ? "Disconnect from Intune tenant" : "Connect to Intune tenant"}>
            <Switch
              checked={isIntuneOn}
              onChange={handleToggle}
              sx={{
                '& .MuiSwitch-track': {
                  backgroundColor: isIntuneOn ? '#1976d2' : '#ccc',
                },
                '& .MuiSwitch-thumb': {
                  backgroundColor: '#fff',
                },
              }}
            />
          </Tooltip>
        }
        label={
          // Always showing the static "Intune" label here — the selected tenant's
          // name/id is intentionally not rendered in place of it for now. selectedTenant
          // itself is still tracked (Redux + authStorage, see selectTenant/restoreSelectedTenant
          // above) so it's available again whenever this label is revisited.
          <Typography
            variant="body1"
            noWrap
            sx={{ color: "#fff", maxWidth: 140 }}
          >
            Intune
          </Typography>
        }
      />

      {errorMessage && (
        <Typography variant="caption" sx={{ color: "#f44336", ml: 1 }}>
          {errorMessage}
        </Typography>
      )}

      <Dialog
        open={tenantPopupOpen}
        onClose={() => {
          setTenantPopupOpen(false);
          cancelEditing();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "4px",
            backgroundColor: "#1b1a19",
          },
        }}
      >

        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "8px",
            backgroundColor: "#1b1a19",
            color: "#ffffff",
          }}
        >
          <Typography variant="h6" fontWeight={600} color="#ffffff">
            Select Tenant
          </Typography>
          <IconButton
            onClick={() => {
              setTenantPopupOpen(false);
              cancelEditing();
            }}
            size="small"
            sx={{ color: "#ffffff" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "#3b3a39" }} />
        <DialogContent sx={{ paddingTop: 1, backgroundColor: "#1b1a19" }}>
          {allowedTenants.length === 0 ? (
            <Typography
              align="center"
              sx={{ mt: 2, color: "#8a8886" }}
            >
              No tenants found. Please contact your administrator.
            </Typography>
          ) : (
            <List>
              {allowedTenants.map((tenant) => {
                const isEditing = editingTenantId === tenant.tenantId;
                const isSaving = savingTenantId === tenant.tenantId;

                return (
                  <ListItem
                    key={tenant.tenantId}
                    disablePadding
                    sx={{ borderRadius: "10px", marginBottom: "6px" }}
                    secondaryAction={
                      isGlobalAdmin && !isEditing ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(tenant);
                          }}
                          sx={{ color: "#8a8886" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : undefined
                    }
                  >
                    {isEditing ? (
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%", padding: "10px 14px" }}>
                        <TextField
                          autoFocus
                          fullWidth
                          size="small"
                          label="Tenant name"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          disabled={isSaving}
                          error={editValue.length > MAX_TENANT_NAME_LENGTH}
                          helperText={`${editValue.length}/${MAX_TENANT_NAME_LENGTH}`}
                          sx={{
                            "& .MuiInputLabel-root": { color: "#ffffff" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#1976d2" },
                            "& .MuiOutlinedInput-root": {
                              color: "#ffffff",
                              "& fieldset": { borderColor: "rgb(208, 208, 208)" },
                              "&:hover fieldset": { borderColor: "#1976d2" },
                              "&.Mui-focused fieldset": { borderColor: "#1976d2" },
                            },
                            "& .MuiFormHelperText-root": { color: "#8a8886" },
                          }}
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={isSaving || editValue.length > MAX_TENANT_NAME_LENGTH}
                          onClick={() => saveTenantDisplayName(tenant)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={isSaving} onClick={cancelEditing} sx={{ color: "#ffffff" }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <ListItemButton
                        onClick={() => handleSelectTenant(tenant)}
                        sx={{
                          borderRadius: "10px",
                          padding: "10px 14px",

                          '&.Mui-selected': {
                            backgroundColor: "#3c3c3c",
                          },

                          '&:hover': {
                            backgroundColor: "#2a2a2a",
                          },
                        }}
                      >
                        {(() => {
                          const displayName = tenant.tenantDisplayName;
                          const isTruncated = !!displayName && displayName.length > SELECTED_TENANT_NAME_DISPLAY_LENGTH;

                          return (
                            <Tooltip title={isTruncated ? displayName! : ""}>
                              <ListItemText
                                primary={displayName ? truncate(displayName, SELECTED_TENANT_NAME_DISPLAY_LENGTH) : "(Name: not available)"}
                                secondary={tenant.tenantId}
                                primaryTypographyProps={{ sx: { color: "#ffffff" } }}
                                secondaryTypographyProps={{ sx: { color: "#8a8886" } }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </ListItemButton>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>

      </Dialog>
    </>
  );
}